import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { appSettings } from 'app/appSettings';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { Observable, of } from 'rxjs';
import { RootState } from '../';
import { AzureMonitoringService } from '../../services/AzureMonitoringService';
import { FlowRestService } from '../../services/rest/flow.rest.service';
import { util } from '../../util/util';
import { AbstractEffects } from '../AbstractEffects';
// eslint-disable-next-line no-duplicate-imports
import * as actions from './flows.actions';
import { FlowsAcceptFlowStartAction, FlowsCancelFlowStartAction, FlowsDownloadAction, FlowsLoadAction } from './flows.actions';
import { goAction } from '../router/router.actions';
import { catchError, debounceTime, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { NotificationService } from 'app/core/services/notification.service';
import moment from 'moment';
import { AuthenticationService } from '../../authentication/authentication.service';

@Injectable({ providedIn: 'root' })
export class FlowsEffects extends AbstractEffects {
  private authenticationService = inject(AuthenticationService);
  loadFlows$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<FlowsLoadAction>(actions.FLOWS_LOAD),
      debounceTime(50),
      withLatestFrom(this.store, (action, state) => {
        return { state };
      }),
      switchMap(({ state }) => {
        let customers = null;
        let isSuperUser = false;
        if (state.session.userContext.isSuperUser) {
          customers = state.session.customerPermissions.data.queryOrGetFlow;
          isSuperUser = state.session.userContext.isSuperUser;
        }
        return this.flowRestService
          .getFlows(state.flows.filter, customers) // Request flows
          .pipe(
            map(flows => {
              const filterOnMultipleCustomers = isSuperUser
                ? state.flows.filter.customers === null
                  ? true
                  : state.flows.filter.customers.length > 1 || state.flows.filter.customers.length === 0
                : false;
              return new actions.FlowsLoadSuccessAction({
                flows,
                definitions: state.definitions,
                places: state.contractDetails.places,
                handshakes: state.contractInfo.handshakes,
                filterOnMultipleCustomers,
              });
            })
          );
      }),
      catchError(err => this.handleFail(err, 'FLOWS.NOTIFICATIONS.FIND_FLOWS', [of(new actions.FlowsLoadFailAction(err))]))
    )
  );
  acceptFlow$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<FlowsAcceptFlowStartAction>(actions.FLOWS_ACCEPT_FLOW_START),
      withLatestFrom(this.store, (action: FlowsAcceptFlowStartAction, state) => {
        return { flow: action.payload.flow, statusses: state.definitions.flow.status };
      }),
      switchMap(({ statusses, flow }) =>
        this.flowRestService.acceptFlow(flow).pipe(
          map(json => {
            json.status = statusses.find(status => status.id == json.status);
            return new actions.FlowsAcceptFlowSuccessAction(json);
          }),
          catchError(err => this.handleFail(err, 'FLOWS.NOTIFICATIONS.ACCEPT_FLOW', [of(new actions.FlowsAcceptFlowFailAction(util.getErrorMessage(err)))]))
        )
      )
    )
  );
  /**
   * Refresh flow list after successful accept or cancel flow.
   */

  acceptOrCancelSuccessFlow$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.FLOWS_ACCEPT_FLOW_SUCCESS, actions.FLOWS_CANCEL_FLOW_SUCCESS),
      switchMap(() => {
        return of(goAction({ path: '/flows/refresh' }));
      })
    )
  );

  cancelFlow$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<FlowsCancelFlowStartAction>(actions.FLOWS_CANCEL_FLOW_START),
      withLatestFrom(this.store, (action: FlowsCancelFlowStartAction, state) => {
        return { flow: action.payload.flow, statusses: state.definitions.flow.status };
      }),
      switchMap(({ statusses, flow }) =>
        this.flowRestService.cancelFlow(flow).pipe(
          map(json => {
            json.status = statusses.find(status => status.id == json.status); // TODO check if == or ===
            return new actions.FlowsCancelFlowSuccessAction(json);
          }),
          catchError(err => this.handleFail(err, 'FLOWS.NOTIFICATIONS.CANCEL_FLOWS', [of(new actions.FlowsCancelFlowFailAction(err))]))
        )
      )
    )
  );

  downloadFlowsFile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<FlowsDownloadAction>(actions.FLOWS_DOWNLOAD),
      debounceTime(50),
      withLatestFrom(this.store, (action, state) => {
        return { action, state };
      }),
      switchMap(({ action, state }: { action: FlowsDownloadAction; state: RootState }) => {
        let customers: Customer[] = null;
        if (state.session.userContext.isSuperUser) {
          customers = state.session.customerPermissions.data.queryOrGetFlow;
        }
        return this.flowRestService.getFlowsAsDocument(action.payload.filter, action.payload.totalItems, action.payload.exportType, customers).pipe(
          map((blob: Blob) => {
            const timestamp = moment().format('YYYYMMDDHHmmss');
            return new actions.FlowsDownloadSuccessAction({ filename: `flows-${timestamp}.xlsx`, blob });
          }),
          catchError(err => this.handleFail(err, 'FLOWS.NOTIFICATIONS.DOWNLOAD_FILE', [of(new actions.FlowsDownloadFailAction(err))]))
        );
      })
    )
  );
  loadLatestFlows$: Observable<{}> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.DASHBOARD_TILE_LATEST_FLOWS_LOAD),
      withLatestFrom(this.store, (action, state) => {
        return { state };
      }),
      switchMap(({ state }) => {
        return this.flowRestService.getLatestFlows(state.session.selectedCustomer.loadSuccess && state.session.selectedCustomer.data?.id).pipe(
          map(flows => {
            return new actions.DasboardTileLatestFlowsLoadSuccess({ flows });
          }),
          catchError(err => {
            return of(new actions.DasboardTileLatestFlowsLoadFail(err));
            // this.handleFail(err, 'Get Latest Flows', [of(new actions.DasboardTileLatestFlowsLoadFail(err))])
          })
        );
      })
    )
  );
  loadFlowTypes$: Observable<{}> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.FLOW_TYPES_LOAD),
      withLatestFrom(this.authenticationService.authenticated$, this.store, (action, isAuthenticated, state) => ({
        isAuthenticated,
        state,
      })),
      filter(({ isAuthenticated }: { isAuthenticated: boolean }) => isAuthenticated),
      switchMap(({ state }: { state: RootState; isAuthenticated: boolean }) => {
        return this.flowRestService.getFlowTypes(state.session.selectedCustomer.loadSuccess && state.session.selectedCustomer.data?.id).pipe(
          map(flowTypes => {
            return new actions.GetFlowTypesLoadSuccessAction({ ...flowTypes, definitions: state.definitions });
          }),
          catchError(err => {
            return this.handleFail(err, 'Get Order Types', [of(goAction({ path: appSettings.DEFAULT_ROUTE })), of(new actions.GetFlowTypesLoadFailAction(err))]);
          })
        );
      })
    )
  );

  constructor(
    private store: Store<RootState>,
    azureMonitoringService: AzureMonitoringService,
    private actions$: Actions,
    private flowRestService: FlowRestService,
    translateService: TranslateService,
    notificationService: NotificationService
  ) {
    super(translateService, azureMonitoringService, notificationService);
  }
}
